import React, { useEffect, useState } from "react";
import { Card, Table, Button, Modal, Form, DatePicker, Input, Space, message, Popconfirm, Calendar, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import PageHeader from "../../components/ui/PageHeader";
import StatusTag from "../../components/ui/StatusTag";
import EmptyState from "../../components/ui/EmptyState";
import ViewToggle from "../../components/ui/ViewToggle";
import { CalendarHeader } from "../Attendance/MyMonthTab";

const { RangePicker } = DatePicker;

const WFH_STATUS_COLORS = { pending: "#F59E0B", approved: "#14B8A6", rejected: "#EF4444" };

const WFH = () => {
  const { request } = useRequest();
  const [requests, setRequests] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState("list");
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchMine = () => {
    request({ url: apiPath.wfhRequestsMine, method: "GET", onSuccess: (res) => setRequests(res?.data?.data || []) });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchMine, []);

  const openCreate = () => {
    form.resetFields();
    setSubmitting(false);
    setModalOpen(true);
  };

  const onSubmit = () => {
    // Without this guard, a slow request + no visible feedback on the OK
    // button led to users clicking it repeatedly, creating duplicates.
    if (submitting) return;
    form.validateFields().then((values) => {
      const [start, end] = values.range;
      setSubmitting(true);
      request({
        url: apiPath.wfhRequests,
        method: "POST",
        data: { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD"), reason: values.reason },
        onSuccess: () => {
          setSubmitting(false);
          message.success("WFH request submitted");
          setModalOpen(false);
          fetchMine();
        },
        onError: (err) => {
          setSubmitting(false);
          message.error(err?.response?.data?.message || "Could not submit WFH request");
        },
      });
    });
  };

  const onCancel = (record) => {
    request({
      url: `${apiPath.wfhRequests}/${record._id}/cancel`,
      method: "PUT",
      onSuccess: () => {
        message.success("WFH request cancelled");
        fetchMine();
      },
    });
  };

  const columns = [
    { title: "From", dataIndex: "startDate" },
    { title: "To", dataIndex: "endDate" },
    { title: "Reason", dataIndex: "reason", ellipsis: true },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
    { title: "Comment", dataIndex: "adminComment" },
    {
      title: "Actions",
      render: (_, record) =>
        record.status === "pending" ? (
          <Popconfirm title="Cancel this WFH request?" onConfirm={() => onCancel(record)}>
            <Button size="small" danger>
              Cancel
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const wfhDateCellRender = (value) => {
    const dateStr = value.format("YYYY-MM-DD");
    const matches = requests.filter((r) => r.startDate <= dateStr && r.endDate >= dateStr);
    const isPast = !value.isSameOrAfter(moment(), "day");

    return (
      <div className="i-ems-calendar-cell-content">
        {matches.map((r) => {
          const color = WFH_STATUS_COLORS[r.status] || "#9CA3AF";
          return (
            <Tooltip key={r._id} title={`WFH — ${r.status}`}>
              <div className="i-ems-calendar-tag" style={{ color, background: `${color}1A` }}>
                WFH
              </div>
            </Tooltip>
          );
        })}
        {!isPast && (
          <div className="i-ems-calendar-apply-wrap">
            <Button
              size="small"
              type="primary"
              className="i-ems-calendar-apply-btn"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openCreate();
                form.setFieldsValue({ range: [value, value] });
              }}
            >
              Apply
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageTransition>
      <PageHeader
        title="Work From Home"
        subtitle="Manage your WFH requests"
        actions={<ViewToggle value={view} onChange={setView} />}
      />
      {view === "list" ? (
        <Card>
          <Space style={{ marginBottom: 16, width: "100%", justifyContent: "flex-end" }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Request WFH
            </Button>
          </Space>
          <Table
            rowKey="_id"
            columns={columns}
            dataSource={requests}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <EmptyState message="No WFH requests yet" actionLabel="Request WFH" onAction={openCreate} /> }}
          />
        </Card>
      ) : (
        <Card>
          <div className="i-ems-apply-calendar">
            <Calendar dateCellRender={wfhDateCellRender} headerRender={({ value: v, onChange }) => <CalendarHeader value={v} onChange={onChange} />} />
          </div>
        </Card>
      )}

      <Modal
        title="Request WFH"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSubmit}
        confirmLoading={submitting}
        okButtonProps={{ disabled: submitting }}
        cancelButtonProps={{ disabled: submitting }}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item name="range" label="Date Range" rules={[{ required: true }]}>
            <RangePicker style={{ width: "100%" }} disabledDate={(current) => current && current < moment().startOf("day")} />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default WFH;
